import { OptionTooltip } from "@/workflow/components/configurations/form/components/OptionTooltip";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import { useState, useEffect } from "react";
import { useCheckAutoID } from "@/queries/digital-files/registries.api";
import { useDebounceHook } from "@/hooks/use-debounce";

interface IdFieldProps {
  value?: string;
  label: string;
  required?: boolean;
  canUnlock?: boolean;
  onChange?: (value: string) => void;
  entityId?: string;
  currentRegistryId?: string;
  originalValue?: string;
}

type ValidationState = "idle" | "validating" | "valid" | "invalid";

export const IdField = ({
  value,
  label,
  required,
  canUnlock,
  onChange,
  entityId,
  currentRegistryId,
  originalValue,
}: IdFieldProps) => {
  const [locked, setLocked] = useState(true);
  const [validationState, setValidationState] =
    useState<ValidationState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [internalValue, setInternalValue] = useState(value || "");
  const debouncedValue = useDebounceHook(internalValue, 500);
  const { mutateAsync: checkAutoID } = useCheckAutoID();

  // Sync internal value with prop value when it changes externally
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  // Validate when debounced value changes
  useEffect(() => {
    const validateId = async () => {
      if (locked) {
        return;
      }

      if (!debouncedValue || !entityId) {
        setValidationState("idle");
        setErrorMessage("");
        return;
      }

      // Si el valor no ha cambiado respecto al original, no validar
      if (debouncedValue === value || debouncedValue === originalValue) {
        setValidationState("idle");
        setErrorMessage("");
        return;
      }

      // Start validation
      setValidationState("validating");

      try {
        const result = await checkAutoID(debouncedValue);
        if (!result.data.is_available) {
          setValidationState("invalid");
          setErrorMessage("Este ID ya está en uso");
        } else if (!result.data.is_valid) {
          setValidationState("invalid");
          setErrorMessage("Este ID es inválido");
        } else {
          // valid ID
          if (onChange && debouncedValue !== value) {
            onChange(debouncedValue);
          }
          setValidationState("valid");
          setErrorMessage("");
        }
      } catch (error) {
        console.error("Error validating ID:", error);
        setValidationState("invalid");
        setErrorMessage("Error al validar el ID");
      }
    };

    validateId();
  }, [
    debouncedValue,
    locked,
    entityId,
    currentRegistryId,
    originalValue,
    checkAutoID,
    value,
    onChange,
  ]);

  return (
    <GroupTextField
      label={label}
      required={required}
      error={validationState === "invalid" ? errorMessage : undefined}
      helperText={
        !locked && validationState === "idle"
          ? "Identificador válido"
          : undefined
      }
    >
      <GroupTextFieldRow>
        <div className="flex items-center w-full relative">
          <Input
            className="w-full pr-32 rounded-br-md!"
            placeholder="Identificador único"
            value={internalValue}
            disabled={locked}
            onChange={(e) => setInternalValue(e.target.value)}
          />
          <div className="absolute right-0 top-0 bottom-0 flex items-center gap-2">
            {validationState === "validating" && (
              <Icon
                name="spinner-third"
                type="far"
                className="animate-spin text-body-quiet"
              />
            )}
            <OptionTooltip title="El ID es el identificador único del registro. Se recomienda no modificarlo si ya se ha utilizado en integraciones o automatizaciones.">
              <div className="text-body-quiet cursor-help">
                <Icon name="circle-info" type="far" />
              </div>
            </OptionTooltip>
            <Button
              variant="quiet"
              type="button"
              disabled={!canUnlock}
              onClick={() => canUnlock && setLocked(!locked)}
              aria-label={
                locked
                  ? "Desbloquear edición de key"
                  : "Bloquear edición de key"
              }
              className="h-full px-3 bg-neutral-object border-l border-t border-b border-neutral-quiet rounded-br-md rounded-tl-none rounded-tr-md rounded-bl-none flex items-center justify-center"
            >
              <Icon
                name={locked ? "lock" : "lock-open"}
                type="far"
                className="text-body-quiet text-sm"
              />
            </Button>
          </div>
        </div>
      </GroupTextFieldRow>
    </GroupTextField>
  );
};
